import type { CalendarPhoto, PhotoQualityStatus } from '../types/calendarProject';

const previewSupportedTypes = ['image/jpeg', 'image/png', 'image/webp'];
const screenshotWords = ['screenshot', 'snímek', 'snimek', 'screen', 'obrazovky'];

function createPhotoId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `photo_${crypto.randomUUID()}`;
  }

  return `photo_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function clampScore(score: number) {
  return Math.max(0, Math.min(100, Math.round(score)));
}

function getQualityStatus(score: number): PhotoQualityStatus {
  if (score >= 75) return 'good';
  if (score >= 45) return 'warning';
  return 'bad';
}

function getOrientation(width: number, height: number): CalendarPhoto['orientation'] {
  if (!width || !height) return 'square';
  if (Math.abs(width - height) <= Math.max(width, height) * 0.08) return 'square';
  return width > height ? 'landscape' : 'portrait';
}

function loadImageDimensions(objectUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => reject(new Error('Image dimensions failed'));
    image.src = objectUrl;
  });
}

export function scorePhoto(photo: Pick<CalendarPhoto, 'width' | 'height' | 'sizeBytes' | 'orientation' | 'issues'>) {
  let score = 100;
  const shortestSide = Math.min(photo.width, photo.height);
  const longestSide = Math.max(photo.width, photo.height);

  if (!photo.width || !photo.height) score -= 40;
  else if (shortestSide < 1200) score -= 35;
  else if (shortestSide < 2000) score -= 15;

  if (longestSide > 3000) score += 10;
  if (photo.sizeBytes < 300 * 1024) score -= 20;
  if (photo.sizeBytes > 1024 * 1024) score += 5;
  if (photo.orientation === 'landscape') score += 8;
  if (photo.orientation === 'portrait') score += 3;
  if (photo.orientation === 'square') score += 4;
  if (photo.issues.includes('Možná duplicita')) score -= 10;
  if (photo.issues.includes('Nepodporovaný formát náhledu')) score -= 25;
  if (photo.issues.includes('Nepodařilo se načíst rozměry')) score -= 25;
  if (photo.issues.includes('Vypadá jako screenshot – ověř tiskovou kvalitu.')) score -= 15;
  if (photo.issues.includes('Neobvyklý poměr stran – může být nutný ruční ořez.')) score -= 12;

  return clampScore(score);
}

export async function analyzeImageFile(file: File): Promise<CalendarPhoto> {
  const objectUrl = URL.createObjectURL(file);
  const issues: string[] = [];
  let width = 0;
  let height = 0;

  if (!previewSupportedTypes.includes(file.type)) {
    issues.push('Nepodporovaný formát náhledu');
  }

  try {
    if (previewSupportedTypes.includes(file.type)) {
      const dimensions = await loadImageDimensions(objectUrl);
      width = dimensions.width;
      height = dimensions.height;
    }
  } catch {
    issues.push('Nepodařilo se načíst rozměry');
  }

  if (width && height && Math.min(width, height) < 1200) {
    issues.push('Nízké rozlišení');
  }

  if (file.size < 300 * 1024) {
    issues.push('Velmi malý soubor');
  }

  const normalizedName = file.name.toLocaleLowerCase('cs-CZ');
  if (screenshotWords.some((word) => normalizedName.includes(word))) {
    issues.push('Vypadá jako screenshot – ověř tiskovou kvalitu.');
  }

  if (width && height && Math.max(width / height, height / width) > 2.5) {
    issues.push('Neobvyklý poměr stran – může být nutný ruční ořez.');
  }

  const orientation = getOrientation(width, height);
  const score = scorePhoto({ width, height, sizeBytes: file.size, orientation, issues });

  return {
    id: createPhotoId(),
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    width,
    height,
    objectUrl,
    orientation,
    score,
    qualityStatus: getQualityStatus(score),
    issues,
    selectedForDraft: false,
    uploadedAt: new Date().toISOString(),
  };
}

export function pickBestPhotos(photos: CalendarPhoto[], count = 13): CalendarPhoto[] {
  return [...photos]
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return b.sizeBytes - a.sizeBytes;
    })
    .slice(0, count);
}

export function markPossibleDuplicates(photos: CalendarPhoto[]) {
  const duplicateCounts = photos.reduce((counts, photo) => {
    const duplicateKey = `${photo.fileName}-${photo.sizeBytes}`;
    counts.set(duplicateKey, (counts.get(duplicateKey) ?? 0) + 1);
    return counts;
  }, new Map<string, number>());

  return photos.map((photo) => {
    const duplicateKey = `${photo.fileName}-${photo.sizeBytes}`;
    if ((duplicateCounts.get(duplicateKey) ?? 0) < 2) return photo;

    const issues = photo.issues.includes('Možná duplicita') ? photo.issues : [...photo.issues, 'Možná duplicita'];
    const score = scorePhoto({ ...photo, issues });

    return {
      ...photo,
      issues,
      score,
      qualityStatus: getQualityStatus(score),
    };
  });
}
